import { FormElement } from '../FormElement';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCallback, useMemo, useState } from 'react';
import { toast } from '@/hooks/useToast';
import { FormSection } from '../FormSection';
import Button from '../core/Button';
import { IconCheck } from '@/icons';
import {
  TravellerWithCollectionMembershipsDTO,
  useInactivateCollectionMember,
  useActivateCollectionMember,
  getGetTravellerByIDQueryKey,
  getGetTravellersForDepartmentQueryKey,
} from '@/api/trafikportalen';
import { useQueryClient } from '@tanstack/react-query';
import Input from '../core/Input';
import { DateTime } from 'luxon';
import { checkUserPermissions } from '../Auth/checkUserPermissions';

interface TravellerInactivateFormProps {
  traveller: TravellerWithCollectionMembershipsDTO;
  mode: 'activate' | 'inactivate';
}

export const TravellerInactivateForm = ({
  traveller,
  mode,
}: TravellerInactivateFormProps) => {
  const { formatMessage } = useIntl();
  const inactivateCollectionMember = useInactivateCollectionMember();
  const activateCollectionMember = useActivateCollectionMember();
  const queryClient = useQueryClient();

  const [editMode, setEditMode] = useState<boolean>(false);
  const [inactivateAt, setInactivateAt] = useState<string>('');

  const isActivation = mode === 'activate';

  const hasNonSchoolMemberships = useMemo(() => {
    return traveller.memberships.some(
      ({ collection_type }) => collection_type === 'master',
    );
  }, [traveller.memberships]);

  const hasScheduledInactivation = traveller.memberships.some(
    ({ active, inactivate_at }) => active && !!inactivate_at,
  );

  // Resources are only dropped once a membership is actually inactivated,
  // so cancelling a scheduled inactivation keeps the traveller's tickets.
  const hasInactivatedNonSchoolMemberships = traveller.memberships.some(
    ({ active, collection_type }) => !active && collection_type === 'master',
  );

  const startEdit = useCallback(() => {
    setEditMode(true);
  }, []);

  const cancelEdit = useCallback(() => {
    setInactivateAt('');
    setEditMode(false);
  }, []);

  const onSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();

      if (!traveller?.memberships?.length) {
        return;
      }

      const inactivateAtDate = inactivateAt
        ? DateTime.fromFormat(inactivateAt, 'yyyy-MM-dd').endOf('day').toISO()
        : undefined;

      try {
        if (isActivation) {
          await Promise.all(
            traveller.memberships.map((membership) => {
              return activateCollectionMember.mutateAsync({
                collectionMemberID: membership.collection_member_id,
              });
            }),
          );
        } else {
          await Promise.all(
            traveller.memberships.map((membership) => {
              if (!membership.active) {
                return;
              }

              return inactivateCollectionMember.mutateAsync({
                collectionMemberID: membership.collection_member_id,
                data: {
                  inactivate_at: inactivateAtDate ?? undefined,
                },
              });
            }),
          );
        }

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: getGetTravellerByIDQueryKey(traveller.traveller.id),
          }),
          ...traveller.memberships.map((membership) =>
            queryClient.invalidateQueries({
              queryKey: getGetTravellersForDepartmentQueryKey(
                membership.department_id,
              ),
            }),
          ),
        ]);

        toast.success(
          {
            title: formatMessage({
              defaultMessage: 'Ändringar sparade',
            }),
          },
          { autoClose: 3000 },
        );

        cancelEdit();
      } catch (error) {
        console.error(error);
        toast.error(
          {
            title: formatMessage({
              defaultMessage: 'Ett fel uppstod',
            }),
          },
          { autoClose: 3000 },
        );
      }
    },
    [
      inactivateAt,
      traveller,
      isActivation,
      activateCollectionMember,
      inactivateCollectionMember,
      queryClient,
      formatMessage,
      cancelEdit,
    ],
  );

  const onChangeDate = useCallback((date: string) => {
    setInactivateAt(date);
  }, []);

  if (!traveller?.memberships?.length) {
    return null;
  }

  return (
    <form onSubmit={onSubmit}>
      <FormSection
        title={
          isActivation ? (
            <FormattedMessage defaultMessage="Aktivera" />
          ) : (
            <FormattedMessage defaultMessage="Inaktivera" />
          )
        }
        buttonType="lockButton"
        onClickButton={startEdit}
        editMode={editMode}
        canSeeButton={checkUserPermissions({
          permission: 'organization:editor',
        })}
      >
        <span className="mb-2 text-base font-normal">
          {isActivation ? (
            <>
              {hasScheduledInactivation ? (
                <FormattedMessage defaultMessage="Vid aktivering tas den schemalagda inaktiveringen bort och samtliga medlemskap för resenären återställs i alla avdelningar." />
              ) : (
                <FormattedMessage defaultMessage="Vid aktivering återställs samtliga medlemskap för resenären i alla avdelningar." />
              )}{' '}
              {hasInactivatedNonSchoolMemberships && (
                <FormattedMessage defaultMessage="Biljetter och tjänsteresor måste manuellt läggas till igen." />
              )}
            </>
          ) : (
            <>
              <FormattedMessage defaultMessage="Vid inaktivering avslutas biljetter köpta i portalen." />{' '}
              {hasNonSchoolMemberships && (
                <FormattedMessage defaultMessage="Även tjänsteresa i app och tjänstresa på kort avslutas för resenären i alla avdelningar (kan upp till 5 minuter). Biljetter köpta på kort eller i app avslutas inte." />
              )}
            </>
          )}
        </span>
        {editMode && !isActivation && (
          <>
            <span className="text-base font-normal">
              <FormattedMessage defaultMessage="Ange vid vilket datum resenären ska inaktiveras. Om du inte anger ett datum kommer resenären att inaktiveras direkt. Det angivna datumet blir resenärens sista aktiva dag i organisationen." />
            </span>
            <div className="flex flex-col gap-2">
              <FormElement
                label={<FormattedMessage defaultMessage="Inaktiveringsdatum" />}
                input={
                  <Input
                    id="inactivate_at"
                    name="inactivate_at"
                    type="date"
                    value={inactivateAt}
                    min={new Date().toISOString().split('T')[0]}
                    disabled={!editMode}
                    onChange={(e) => onChangeDate(e.target.value)}
                  />
                }
              />
            </div>
          </>
        )}
        {editMode && isActivation && (
          <span className="text-base font-normal">
            <FormattedMessage defaultMessage="Klicka på knappen nedan för att aktivera resenären." />
          </span>
        )}
        {!editMode && (
          <span className="text-base font-normal italic">
            <FormattedMessage defaultMessage="Lås upp för att fortsätta." />
          </span>
        )}
        {editMode && (
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={cancelEdit}>
              <FormattedMessage defaultMessage="Avbryt" />
            </Button>
            <Button
              color={isActivation ? 'green' : 'yellow'}
              type="submit"
              variant="filled"
              startIcon={<IconCheck />}
              loading={
                isActivation
                  ? activateCollectionMember.isPending
                  : inactivateCollectionMember.isPending
              }
            >
              {isActivation ? (
                <FormattedMessage defaultMessage="Aktivera" />
              ) : (
                <FormattedMessage defaultMessage="Inaktivera" />
              )}
            </Button>
          </div>
        )}
      </FormSection>
    </form>
  );
};
